import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Item, ItemContent, ItemDescription, ItemTitle } from '../ui/item';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';
import { removeBackground as removeBackgroundUtil } from '../../utils/backgroundRemoval';

const VectorizeModal = ({ 
  isOpen, 
  onClose, 
  onContinue, 
  onRequestQuote, 
  filename, 
  isProcessing = false,
  hasBackground = false,
  previewImage = null 
}) => {
  const [removeBackground, setRemoveBackground] = useState(true);
  const [processedPreview, setProcessedPreview] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // Generate transparent preview when modal opens
  useEffect(() => {
    if (hasBackground && previewImage && isOpen) {
      setIsGeneratingPreview(true);
      removeBackgroundUtil(previewImage, {
        tolerance: 30,
        autoDetect: true
      })
        .then(result => {
          setProcessedPreview(result);
          setIsGeneratingPreview(false);
        })
        .catch(error => {
          console.error('Error generating preview:', error);
          setProcessedPreview(null);
          setIsGeneratingPreview(false);
        });
    }
  }, [hasBackground, previewImage, isOpen]);
  const handleOpenChange = (open) => {
    if (!open) {
      // Reset state when closing
      setRemoveBackground(true);
      setProcessedPreview(null);
      setIsGeneratingPreview(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Tyvärr stöder vi inte pixelgrafik, men vi kan vektorisera din grafik åt dig</DialogTitle>
          <DialogDescription>
            Vi hjälper dig att vektorisera din logotyp eller grafik, vilket innebär att bilden omvandlas till vektorfiler som kan förstoras och förminskas utan att tappa skärpa. Genom att arbeta med vektorgrafik säkerställer vi att din design alltid ser skarp ut och behåller sina färger och former oavsett storlek.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Item variant="muted">
            <ItemContent>
              <ItemTitle>Grafik som ska vektoriseras:</ItemTitle>
              <ItemDescription>{filename}</ItemDescription>
            </ItemContent>
          </Item>

          {hasBackground && previewImage && (
            <div className="space-y-3">
              <div className="p-3 border rounded-lg bg-muted/50">
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <span className="text-yellow-600">⚠️</span>
                  Din grafik har en enfärgad bakgrund
                </p>
                <div className="bg-slate-200 rounded-md p-4 flex items-center justify-center relative min-h-[150px]">
                  {isGeneratingPreview ? (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Förbereder förhandsvisning...</span>
                    </div>
                  ) : (
                    <img 
                      src={removeBackground && processedPreview ? processedPreview : previewImage} 
                      alt="Preview" 
                      className="max-h-32 object-contain transition-opacity duration-200"
                      key={removeBackground ? 'transparent' : 'original'}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {removeBackground 
                    ? 'Så här kommer grafiken se ut med transparent bakgrund' 
                    : 'Så här kommer grafiken se ut med bakgrunden kvar'}
                </p>
              </div>

              <div className="flex items-start space-x-3 p-3 border rounded-lg">
                <Checkbox 
                  id="remove-bg" 
                  checked={removeBackground}
                  onCheckedChange={setRemoveBackground}
                  disabled={isProcessing || isGeneratingPreview}
                />
                <div className="space-y-1">
                  <Label 
                    htmlFor="remove-bg" 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Ta bort bakgrund automatiskt (rekommenderas)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Vi tar bort enfärgade bakgrunder så att din grafik får transparent bakgrund. Ingår i vektoriseringen.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="secondary" disabled={isProcessing}>
            Avbryt
          </Button>
          <Button onClick={() => onContinue(removeBackground)} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {removeBackground ? 'Tar bort bakgrund...' : 'Bearbetar...'}
              </>
            ) : (
              'Fortsätt (1 500kr extra)'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VectorizeModal; 